/**
 * useGazeDetection.js
 * ====================
 * React hook that wires together:
 *   1. Webcam access via the browser's getUserMedia API
 *   2. MediaPipe FaceLandmarker (runs in-browser, no network needed)
 *   3. Eye and face crop extraction from detected landmarks
 *   4. HTTP POST of the crops to the GazeNet v9 inference API
 *   5. Returns the latest raw gaze direction prediction
 *
 * What this hook does NOT do:
 *   - Temporal smoothing (debouncing) — that's in useGazeNavigation.js
 *   - UI navigation logic — also in useGazeNavigation.js
 *
 * Usage:
 *   import { useGazeDetection } from './useGazeDetection';
 *
 *   function MyComponent() {
 *     const { gazeDirection, confidence, videoRef, isReady, error } = useGazeDetection({
 *       enabled: true,
 *     });
 *     // gazeDirection: 'up' | 'down' | 'left' | 'right' | 'straight' | null
 *   }
 *
 * Prerequisites (run from eye2voice-ui/ directory):
 *   npm install @mediapipe/tasks-vision
 *
 * MediaPipe model file:
 *   Copy capstone/face_landmarker.task to eye2voice-ui/public/face_landmarker.task
 *   (or use the CDN URL defined in FACE_LANDMARKER_URL below)
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { FaceLandmarker, FilesetResolver } from '@mediapipe/tasks-vision';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

/**
 * URL of the MediaPipe FaceLandmarker model file.
 *
 * Option A (recommended for local dev): serve from your own public/ folder.
 *   Copy face_landmarker.task to eye2voice-ui/public/face_landmarker.task
 *   Then use: '/face_landmarker.task'
 *
 * Option B: use MediaPipe's CDN (requires internet, no file copy needed).
 *   This URL serves the same float16 model you downloaded locally.
 */
const FACE_LANDMARKER_URL =
  process.env.REACT_APP_FACE_LANDMARKER_URL ||
  '/face_landmarker.task';  // served from public/ folder by React's dev server

/**
 * MediaPipe WASM/JS assets base URL.
 * This is served from the @mediapipe/tasks-vision npm package via CDN.
 * The FilesetResolver downloads the WebAssembly binary from here.
 */
const MEDIAPIPE_WASM_URL =
  'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.32/wasm';

/**
 * Backend inference API URL.
 * Set REACT_APP_INFERENCE_URL in .env.local for local dev:
 *   REACT_APP_INFERENCE_URL=http://localhost:5000
 * In production this will be the Lambda Function URL or API Gateway URL.
 */
const INFERENCE_URL = process.env.REACT_APP_INFERENCE_URL || 'http://localhost:5000';

/**
 * Frames per second to send to the inference API.
 * MediaPipe runs on every requestAnimationFrame (~30 fps on most devices),
 * but we don't need to call the API on every frame. 10 fps is enough for
 * smooth gaze direction detection and reduces API cost and network load.
 */
const INFERENCE_FPS = 10;
const INFERENCE_INTERVAL_MS = 1000 / INFERENCE_FPS;  // 100ms between API calls

// Blink detection thresholds
const EAR_THRESHOLD        = 0.20;  // Eye Aspect Ratio below this = eye is closed
const DOUBLE_BLINK_WINDOW_MS = 500; // two blinks must complete within this window

/**
 * MediaPipe landmark indices for each eye.
 *
 * These indices reference the FaceLandmarker's 478-point mesh.
 * "Right" and "Left" here are from the SUBJECT's perspective (not camera perspective).
 *   - Subject's RIGHT eye appears on the LEFT side of the camera image (mirrored)
 *   - Subject's LEFT  eye appears on the RIGHT side of the camera image
 *
 * GazeNet convention (matching training data):
 *   left_eye  → subject's LEFT eye  → camera RIGHT side → LEFT_EYE_LANDMARKS
 *   right_eye → subject's RIGHT eye → camera LEFT side  → RIGHT_EYE_LANDMARKS
 */
const RIGHT_EYE_LANDMARKS = [33, 160, 158, 133, 153, 144];  // subject's right eye
const LEFT_EYE_LANDMARKS  = [263, 385, 387, 362, 380, 373]; // subject's left eye

// Iris vertical ratio landmarks (subject's perspective).
// Used to analytically detect up/down gaze from MediaPipe — no ML needed.
const IRIS_LM = {
  right: { upper: 159, lower: 145, iris: 468 },
  left:  { upper: 386, lower: 374, iris: 473 },
};


/**
 * How many pixels of padding to add around the eye bounding box.
 * Eye crops need context beyond just the iris/eyelid edges.
 * 30% padding on each side gives good coverage without including the nose/forehead.
 */
const EYE_PADDING_FACTOR = 0.40;  // 40% of the eye bounding box size added as padding

/**
 * How many pixels of padding to add around the face bounding box.
 * 10% gives a small margin without including neck/hair excessively.
 */
const FACE_PADDING_FACTOR = 0.10;


// ─────────────────────────────────────────────────────────────────────────────
// Helper: crop an image region from a canvas
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Extract a rectangular region from a source canvas and return it as a base64 JPEG string.
 *
 * Steps:
 *  1. Create a small offscreen canvas of the target size
 *  2. Use drawImage to copy the source region, automatically scaling it to target size
 *  3. Export the offscreen canvas as a base64 JPEG
 *
 * @param {HTMLCanvasElement} sourceCanvas  The canvas containing the full video frame.
 * @param {number} x      Left edge of the crop region in pixels.
 * @param {number} y      Top edge of the crop region in pixels.
 * @param {number} w      Width of the crop region in pixels.
 * @param {number} h      Height of the crop region in pixels.
 * @param {number} targetW  Width of the output image in pixels (model expects 48 for eyes, 112 for face).
 * @param {number} targetH  Height of the output image in pixels.
 * @returns {string}  Base64-encoded JPEG string (no data URL prefix).
 */
function cropToBase64(sourceCanvas, x, y, w, h, targetW, targetH) {
  // Create a tiny offscreen canvas for the crop destination
  const cropCanvas = document.createElement('canvas');
  cropCanvas.width  = targetW;
  cropCanvas.height = targetH;
  const ctx = cropCanvas.getContext('2d');

  // drawImage(source, sx, sy, sw, sh, dx, dy, dw, dh)
  // Copies the rectangle [sx, sy, sw, sh] from source and draws it scaled
  // to [dx, dy, dw, dh] on the destination canvas.
  ctx.drawImage(
    sourceCanvas,   // source
    x, y, w, h,     // source rect (pixels within the full frame)
    0, 0, targetW, targetH  // destination rect (fills the crop canvas)
  );

  // Export as JPEG at 90% quality. The data URL format is:
  //   'data:image/jpeg;base64,/9j/4AAQSk...'
  // We strip the prefix and return only the base64 payload.
  const dataURL = cropCanvas.toDataURL('image/jpeg', 0.90);
  return dataURL.split(',')[1];  // strip 'data:image/jpeg;base64,'
}


// ─────────────────────────────────────────────────────────────────────────────
// Helper: compute bounding box from a set of landmark indices
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Compute a bounding box (in pixel coordinates) from a subset of face landmarks.
 *
 * MediaPipe returns normalized coordinates in [0, 1].
 * We multiply by image width/height to get pixel coordinates.
 *
 * @param {Array}  landmarks     Array of {x, y, z} objects (normalized [0,1]).
 * @param {number[]} indices     Which landmark indices to use for the bounding box.
 * @param {number} imageWidth    Pixel width of the video frame.
 * @param {number} imageHeight   Pixel height of the video frame.
 * @param {number} padding       Fractional padding to add around the box (e.g., 0.4 = 40%).
 * @returns {{ x, y, w, h }}    Pixel-space bounding box, clamped to image bounds.
 */
function getBoundingBox(landmarks, indices, imageWidth, imageHeight, padding = 0) {
  // Get pixel coordinates for just the landmarks we care about
  const points = indices.map(i => ({
    x: landmarks[i].x * imageWidth,
    y: landmarks[i].y * imageHeight,
  }));

  // Find the min/max x and y coordinates among the selected points
  const xs = points.map(p => p.x);
  const ys = points.map(p => p.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);

  // The tight bounding box (no padding)
  const tightW = maxX - minX;
  const tightH = maxY - minY;

  // Add padding: expand each side by (padding * dimension)
  const padX = tightW * padding;
  const padY = tightH * padding;

  // Final box with padding, clamped so we don't read outside the image
  const x = Math.max(0, minX - padX);
  const y = Math.max(0, minY - padY);
  const w = Math.min(imageWidth  - x, tightW + 2 * padX);
  const h = Math.min(imageHeight - y, tightH + 2 * padY);

  return { x: Math.floor(x), y: Math.floor(y), w: Math.floor(w), h: Math.floor(h) };
}


// ─────────────────────────────────────────────────────────────────────────────
// Helper: Eye Aspect Ratio (EAR) for blink detection
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Compute the average Eye Aspect Ratio across both eyes.
 *
 * EAR = (||p2-p6|| + ||p3-p5||) / (2 * ||p1-p4||)
 *   where p1/p4 are the horizontal eye corners and
 *   p2,p3,p5,p6 are the vertical eyelid landmarks.
 *
 * EAR ≈ 0.30 when the eye is open; drops toward 0 when closed.
 * A value below EAR_THRESHOLD (0.20) indicates a closed eye / blink.
 *
 * Landmark indices used (subject's perspective):
 *   Right eye: [33, 160, 158, 133, 153, 144]  → p1, p2, p3, p4, p5, p6
 *   Left eye:  [263, 385, 387, 362, 380, 373] → p1, p2, p3, p4, p5, p6
 */
function computeEAR(landmarks, W, H) {
  function eyeEAR(p1i, p2i, p3i, p4i, p5i, p6i) {
    const pt   = i => ({ x: landmarks[i].x * W, y: landmarks[i].y * H });
    const dist = (a, b) => Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);
    const [p1, p2, p3, p4, p5, p6] = [p1i, p2i, p3i, p4i, p5i, p6i].map(pt);
    return (dist(p2, p6) + dist(p3, p5)) / (2 * dist(p1, p4));
  }
  const rightEAR = eyeEAR(33,  160, 158, 133, 153, 144);
  const leftEAR  = eyeEAR(263, 385, 387, 362, 380, 373);
  return (rightEAR + leftEAR) / 2;
}


// ─────────────────────────────────────────────────────────────────────────────
// Helper: iris vertical ratio (for up/down override)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Compute where the iris sits vertically within the eye opening.
 *
 *   0.0 → iris at the very top  (looking UP)
 *   0.5 → iris centered          (looking STRAIGHT)
 *   1.0 → iris at the very bottom (looking DOWN)
 *
 * Averaged across both eyes for robustness.
 *
 * @param {Array} landmarks  Array of 478 {x, y, z} objects from MediaPipe (normalised [0,1])
 * @returns {number}  Iris vertical ratio, averaged over both eyes
 */
function computeIrisVerticalRatio(landmarks) {
  function eyeRatio(lm) {
    const upperY = landmarks[lm.upper].y;
    const lowerY = landmarks[lm.lower].y;
    const irisY  = landmarks[lm.iris].y;
    const span   = lowerY - upperY;
    if (span < 1e-6) return 0.5; // degenerate (eye fully closed)
    return Math.max(0, Math.min(1, (irisY - upperY) / span));
  }
  return (eyeRatio(IRIS_LM.right) + eyeRatio(IRIS_LM.left)) / 2;
}

/**
 * Fuse the model's output with the analytic iris vertical ratio.
 * Only overrides for up/down — the model handles left/right reliably.
 *
 * @param {string} modelDirection  Direction from GazeNet: 'up'|'down'|'left'|'right'|'straight'
 * @param {number} modelConfidence Confidence score from GazeNet (0–1)
 * @param {number} irisRatio       Output of computeIrisVerticalRatio()
 * @param {object} calibration     { upThreshold: number, downThreshold: number }
 * @returns {{ direction: string, confidence: number, overridden: boolean }}
 */
function fuseWithIrisOverride(modelDirection, modelConfidence, irisRatio, calibration) {
  const { upThreshold } = calibration;

  // IVR is a vertical-only signal — never override a horizontal prediction.
  if (modelDirection === 'left' || modelDirection === 'right') {
    return { direction: modelDirection, confidence: modelConfidence, overridden: false };
  }

  if (irisRatio < upThreshold && modelDirection !== 'up') {
    return { direction: 'up', confidence: 0.80, overridden: true };
  }
  return { direction: modelDirection, confidence: modelConfidence, overridden: false };
}


// ─────────────────────────────────────────────────────────────────────────────
// Main hook
// ─────────────────────────────────────────────────────────────────────────────

/**
 * useGazeDetection
 *
 * @param {object} options
 * @param {boolean} options.enabled   Set to false to stop the webcam and processing loop.
 *                                    Useful when the user navigates away or hasn't consented.
 *
 * @returns {object}
 *   videoRef      — React ref to attach to a <video> element (hidden or visible)
 *   isReady       — true once MediaPipe is loaded and webcam is streaming
 *   gazeDirection — latest prediction: 'up'|'down'|'left'|'right'|'straight'|null
 *   confidence    — prediction confidence (0.0–1.0), null before first prediction
 *   probabilities — { up: 0.8, down: 0.05, ... }, null before first prediction
 *   error         — string error message, null if no error
 */
export function useGazeDetection({ enabled = true, calibration = null } = {}) {
  // Resolved thresholds — kept in a ref so callInferenceAPI always reads the
  // latest value without needing it in the useCallback dependency array.
  const resolvedCalibrationRef = useRef({ upThreshold: 0.35, downThreshold: 0.72 });
  resolvedCalibrationRef.current = calibration ?? { upThreshold: 0.35, downThreshold: 0.72 };

  // ── Refs ────────────────────────────────────────────────────────────────
  // useRef values persist across renders without causing re-renders when changed.
  // This makes them ideal for mutable values used in async callbacks.

  const videoRef         = useRef(null);  // attached to a <video> DOM element
  const canvasRef        = useRef(null);  // offscreen canvas for drawing video frames
  const faceLandmarkerRef = useRef(null); // MediaPipe FaceLandmarker instance
  const animFrameRef     = useRef(null);  // requestAnimationFrame handle (for cleanup)
  const lastInferenceRef = useRef(0);     // timestamp of last API call (for rate limiting)
  const isFetchingRef    = useRef(false); // prevents overlapping API calls

  // Iris vertical ratio: updated every frame in processFrame, read in callInferenceAPI.
  const irisRatioRef = useRef(0.5); // default = centered

  // Blink detection refs (mutated in the RAF loop — not state to avoid re-renders)
  const blinkActiveRef   = useRef(false); // true while EAR is below threshold
  const lastBlinkTimeRef = useRef(0);     // timestamp of the most recent completed blink

  // ── State ────────────────────────────────────────────────────────────────
  // useState values cause re-renders when updated, making them suitable for
  // values that the parent component needs to display or act on.

  const [isReady,            setIsReady]            = useState(false);
  const [gazeDirection,      setGazeDirection]      = useState(null);
  const [confidence,         setConfidence]         = useState(null);
  const [probabilities,      setProbabilities]      = useState(null);
  const [irisVerticalRatio,  setIrisVerticalRatio]  = useState(0.5); // exposed for calibration
  const [doubleBlinkCount,   setDoubleBlinkCount]   = useState(0); // increments on each double blink
  const [error,              setError]              = useState(null);


  // ── Step 1: Initialize MediaPipe FaceLandmarker ─────────────────────────
  const initMediaPipe = useCallback(async () => {
    try {
      console.log('[GazeDetection] Initializing MediaPipe FaceLandmarker...');

      // FilesetResolver loads the WebAssembly (WASM) binary that MediaPipe needs.
      // The WASM binary is what allows the ML model to run at near-native speed
      // inside the browser. This download (~4 MB) is cached by the browser.
      const vision = await FilesetResolver.forVisionTasks(MEDIAPIPE_WASM_URL);

      // Create the FaceLandmarker with VIDEO mode settings.
      // VIDEO mode is optimized for continuous frame-by-frame detection.
      // It must be called with a monotonically increasing timestamp on each frame.
      const faceLandmarker = await FaceLandmarker.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath: FACE_LANDMARKER_URL,  // the .task model file
          delegate: 'GPU',  // prefer GPU (WebGL) for inference; falls back to CPU
        },
        runningMode: 'VIDEO',        // continuous stream mode
        numFaces: 1,                 // we only care about the primary user's face
        minFaceDetectionConfidence: 0.5,  // minimum score to report a face
        minFacePresenceScore: 0.5,
        minTrackingConfidence: 0.5,
        outputFaceBlendshapes: false,  // not needed; reduces compute
        outputFacialTransformationMatrixes: false,  // not needed
      });

      faceLandmarkerRef.current = faceLandmarker;
      console.log('[GazeDetection] MediaPipe initialized.');
    } catch (err) {
      console.error('[GazeDetection] MediaPipe initialization failed:', err);
      setError(`MediaPipe init failed: ${err.message}`);
    }
  }, []);


  // ── Step 2: Start webcam ─────────────────────────────────────────────────
  const startWebcam = useCallback(async () => {
    try {
      console.log('[GazeDetection] Requesting webcam access...');

      // getUserMedia asks the browser to open the camera and return a MediaStream.
      // The user will see a permission prompt the first time this runs.
      // video: { facingMode: 'user' } selects the front-facing camera on phones.
      if (!navigator.mediaDevices) {
        console.warn('[GazeDetection] navigator.mediaDevices unavailable — requires HTTPS or localhost');
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',   // front camera (selfie mode)
          width:  { ideal: 640 },
          height: { ideal: 480 },
        },
        audio: false,  // we only need video
      });

      if (!videoRef.current) {
        console.warn('[GazeDetection] videoRef not mounted yet');
        return;
      }

      // Attach the webcam stream to the <video> element.
      // The video element renders the live camera feed (can be hidden with CSS).
      videoRef.current.srcObject = stream;

      // Wait for the video element to be ready to play.
      // 'loadeddata' fires when enough data is available to start playing.
      await new Promise((resolve) => {
        videoRef.current.onloadeddata = resolve;
      });

      // Play the video (required for frame access)
      await videoRef.current.play();

      // Create an offscreen canvas that matches the video dimensions.
      // We'll draw each video frame here and use it as the source for crops.
      canvasRef.current = document.createElement('canvas');
      canvasRef.current.width  = videoRef.current.videoWidth;
      canvasRef.current.height = videoRef.current.videoHeight;

      console.log(
        `[GazeDetection] Webcam stream started: ` +
        `${videoRef.current.videoWidth}×${videoRef.current.videoHeight}`
      );

    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setError('Camera permission denied. Please allow camera access and refresh.');
      } else if (err.name === 'NotFoundError') {
        setError('No camera found on this device.');
      } else {
        setError(`Webcam error: ${err.message}`);
      }
      throw err;
    }
  }, []);


  // ── Step 3: Send crops to the inference API ──────────────────────────────
  /**
   * Takes the three image crops as base64 strings, POSTs them to the
   * GazeNet inference endpoint, and updates state with the prediction.
   *
   * @param {string} faceB64      Base64 JPEG of the face crop
   * @param {string} leftEyeB64   Base64 JPEG of the subject's left eye crop
   * @param {string} rightEyeB64  Base64 JPEG of the subject's right eye crop
   */
  const callInferenceAPI = useCallback(async (faceB64, leftEyeB64, rightEyeB64, irisRatio) => {
    // Guard against overlapping requests.
    // If a previous fetch is still in flight, skip this frame.
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    try {
      const response = await fetch(`${INFERENCE_URL}/predict`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          face:       faceB64,
          left_eye:  leftEyeB64,
          right_eye: rightEyeB64,
        }),
      });

      if (!response.ok) {
        // Non-2xx response (e.g., 400 Bad Request, 500 Internal Server Error)
        const errorData = await response.json().catch(() => ({}));
        console.warn('[GazeDetection] API error:', response.status, errorData);
        return;
      }

      // Parse the JSON response body
      // Expected: { direction: 'up', confidence: 0.82, probabilities: {...} }
      const result = await response.json();

      // Fuse model output with iris vertical ratio override.
      // Uses a ref so we always read the latest calibration without stale closures.
      const fused = fuseWithIrisOverride(
        result.direction,
        result.confidence,
        irisRatio,
        resolvedCalibrationRef.current
      );

      if (fused.overridden) {
        console.log(
          `[GazeDetection] Iris override: ${result.direction} → ${fused.direction} ` +
          `(IVR=${irisRatio.toFixed(3)}, upThreshold=${resolvedCalibrationRef.current.upThreshold})`
        );
      }

      setGazeDirection(fused.direction);
      setConfidence(fused.confidence);
      setProbabilities(result.probabilities);

      console.log(
        `[GazeDetection] Prediction: ${fused.direction} ` +
        `(conf=${fused.confidence?.toFixed(3)})`
      );

    } catch (err) {
      // Network error (server not running, CORS failure, etc.)
      // Don't set error state here (would cause constant re-renders); just log.
      console.warn('[GazeDetection] API call failed:', err.message);
    } finally {
      // Always release the lock so the next frame can proceed
      isFetchingRef.current = false;
    }
  }, []);


  // ── Step 4: Per-frame processing loop ───────────────────────────────────
  /**
   * This function runs on every animation frame (~30 fps).
   * It:
   *   1. Draws the current video frame onto the offscreen canvas
   *   2. Passes the canvas to MediaPipe for landmark detection
   *   3. If a face is found, extracts crop coordinates from landmarks
   *   4. Every INFERENCE_INTERVAL_MS, crops and sends to the API
   */
  const processFrame = useCallback(() => {
    const video        = videoRef.current;
    const canvas       = canvasRef.current;
    const faceLandmarker = faceLandmarkerRef.current;

    // Safety checks: all resources must be available
    if (!video || !canvas || !faceLandmarker) {
      animFrameRef.current = requestAnimationFrame(processFrame);
      return;
    }

    if (video.readyState < 2) {
      // readyState < 2 means video data isn't ready yet (e.g., buffering).
      // HTMLMediaElement.HAVE_CURRENT_DATA = 2
      animFrameRef.current = requestAnimationFrame(processFrame);
      return;
    }

    // ── Draw current video frame to canvas ──────────────────────────────
    // This "snapshot" of the video frame is what we'll send to MediaPipe
    // and crop from. drawImage with no source rect draws the full frame.
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // ── Run MediaPipe FaceLandmarker ─────────────────────────────────────
    // detectForVideo() must receive a monotonically increasing timestamp in ms.
    // We use Date.now() which satisfies this requirement.
    const now = Date.now();
    const result = faceLandmarker.detectForVideo(video, now);

    // result.faceLandmarks is an array of detected faces.
    // Each face is an array of 478 { x, y, z } objects with normalized [0,1] coords.
    if (!result.faceLandmarks || result.faceLandmarks.length === 0) {
      // No face detected in this frame — skip inference, schedule next frame
      animFrameRef.current = requestAnimationFrame(processFrame);
      return;
    }

    // Take the first (and only) detected face
    const landmarks = result.faceLandmarks[0];

    // Compute iris vertical ratio on every frame (full RAF rate).
    // Stored in a ref so callInferenceAPI always reads the freshest value.
    irisRatioRef.current = computeIrisVerticalRatio(landmarks);
    setIrisVerticalRatio(irisRatioRef.current);

    // Pixel dimensions — used by both blink detection and inference cropping
    const W = canvas.width;
    const H = canvas.height;

    // ── Blink detection (runs at full RAF rate, ~30–60 fps) ───────────────
    // Compute Eye Aspect Ratio and track blink open/close transitions.
    // Two completed blinks within DOUBLE_BLINK_WINDOW_MS = double blink.
    const ear = computeEAR(landmarks, W, H);
    if (ear < EAR_THRESHOLD && !blinkActiveRef.current) {
      // Eye just closed — start of a blink
      blinkActiveRef.current = true;
    } else if (ear >= EAR_THRESHOLD && blinkActiveRef.current) {
      // Eye just opened — blink completed
      blinkActiveRef.current = false;
      const blinkNow = Date.now();
      if (lastBlinkTimeRef.current > 0 && blinkNow - lastBlinkTimeRef.current < DOUBLE_BLINK_WINDOW_MS) {
        // Second blink within the window — fire a double blink event
        setDoubleBlinkCount(c => c + 1);
        lastBlinkTimeRef.current = 0; // reset so a third blink doesn't chain
      } else {
        lastBlinkTimeRef.current = blinkNow;
      }
    }

    // ── Rate-limit inference API calls ───────────────────────────────────
    // We don't need to call the API on every animation frame (~30 fps).
    // Only call every INFERENCE_INTERVAL_MS (100ms = 10 fps) to save cost.
    const timeSinceLastInference = now - lastInferenceRef.current;
    if (timeSinceLastInference >= INFERENCE_INTERVAL_MS) {
      lastInferenceRef.current = now;

      // ── Extract bounding boxes from landmarks ─────────────────────────

      // All 478 landmarks for the face bounding box.
      // We use all landmark indices (0 to 477) to get the tightest face box.
      const allIndices = Array.from({ length: 478 }, (_, i) => i);
      const faceBox = getBoundingBox(landmarks, allIndices, W, H, FACE_PADDING_FACTOR);

      // Eye bounding boxes from the specific landmark subsets
      const leftEyeBox  = getBoundingBox(landmarks, LEFT_EYE_LANDMARKS,  W, H, EYE_PADDING_FACTOR);
      const rightEyeBox = getBoundingBox(landmarks, RIGHT_EYE_LANDMARKS, W, H, EYE_PADDING_FACTOR);

      // Sanity check: if any box is degenerate (zero or negative size), skip
      if (faceBox.w <= 0 || faceBox.h <= 0 ||
          leftEyeBox.w <= 0 || leftEyeBox.h <= 0 ||
          rightEyeBox.w <= 0 || rightEyeBox.h <= 0) {
        animFrameRef.current = requestAnimationFrame(processFrame);
        return;
      }

      // ── Crop images and encode as base64 ─────────────────────────────
      // Resize to the sizes the model was trained on:
      //   Face: 112×112    Eyes: 48×48
      const faceB64      = cropToBase64(canvas, faceBox.x,      faceBox.y,      faceBox.w,      faceBox.h,      112, 112);
      const leftEyeB64   = cropToBase64(canvas, leftEyeBox.x,   leftEyeBox.y,   leftEyeBox.w,   leftEyeBox.h,   48,  48);
      const rightEyeB64  = cropToBase64(canvas, rightEyeBox.x,  rightEyeBox.y,  rightEyeBox.w,  rightEyeBox.h,  48,  48);

      // ── Send to inference API (async, non-blocking) ───────────────────
      // We don't await here — that would block the animation frame loop.
      // callInferenceAPI handles its own async lifecycle with isFetchingRef.
      callInferenceAPI(faceB64, leftEyeB64, rightEyeB64, irisRatioRef.current);
    }

    // Schedule the next frame (this creates the continuous loop)
    animFrameRef.current = requestAnimationFrame(processFrame);
  }, [callInferenceAPI]);


  // ── Effect: start/stop everything based on `enabled` ────────────────────
  useEffect(() => {
    if (!enabled) return;  // don't start if explicitly disabled (e.g., before consent)

    let active = true;  // flag to prevent state updates after unmount

    async function start() {
      // Initialize MediaPipe and webcam in parallel (both are async)
      await Promise.all([
        initMediaPipe(),
        startWebcam(),
      ]);

      if (!active) return;  // component unmounted while we were waiting

      setIsReady(true);
      setError(null);
      console.log('[GazeDetection] Landmark detection active. Starting frame loop.');

      // Start the per-frame processing loop
      animFrameRef.current = requestAnimationFrame(processFrame);
    }

    start().catch((err) => {
      if (active) {
        console.error('[GazeDetection] Startup failed:', err);
        setError(err.message);
      }
    });

    // Cleanup function: runs when the component unmounts or `enabled` changes to false
    const video = videoRef.current;
    return () => {
      active = false;

      // Cancel the pending animation frame (stops the processing loop)
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
      }

      // Stop all webcam tracks (turns off the camera indicator light)
      if (video?.srcObject) {
        video.srcObject.getTracks().forEach(track => track.stop());
        video.srcObject = null;
      }

      // Close the MediaPipe FaceLandmarker to free GPU/WASM memory
      if (faceLandmarkerRef.current) {
        faceLandmarkerRef.current.close();
        faceLandmarkerRef.current = null;
      }

      console.log('[GazeDetection] Stopped and cleaned up.');
    };
  }, [enabled, initMediaPipe, startWebcam, processFrame]);


  return {
    videoRef,           // attach to: <video ref={videoRef} style={{ display: 'none' }} />
    isReady,            // boolean: true when webcam+MediaPipe are both running
    gazeDirection,      // string: 'up' | 'down' | 'left' | 'right' | 'straight' | null
    confidence,         // float: 0.0–1.0, null before first prediction
    probabilities,      // object: { up, down, left, right, straight }, null before first
    irisVerticalRatio,  // float: 0.0–1.0 current iris position (0=up, 0.5=center, 1=down)
    doubleBlinkCount,   // integer: increments each time a double blink is detected
    error,              // string: error message, null if no error
  };
}
